import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bell, ArrowRight } from "lucide-react";

/** Inline subscribe widget for capturing operator-specific job alerts.
 *  - Tags subscriber with `interests: ["train-driver", operator?]` so the admin
 *    can segment Monday Briefing sends per operator.
 *  - Designed for the /train-driver-jobs cornerstone but works anywhere.
 */
export default function OperatorSubscribeWidget({
  defaultOperator = "any",
  operators = [],
  role = "train-driver",
  heading = "Get notified when a trainee driver role opens",
  subheading = "Pick an operator (or 'Any') and we'll email you the moment a matching trainee train driver vacancy is posted on Railway Careers UK.",
}) {
  const [email, setEmail] = useState("");
  const [operator, setOperator] = useState(defaultOperator);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const interests = [`role:${role}`];
      if (operator && operator !== "any") interests.push(`operator:${operator}`);
      const r = await api.post("/newsletter/subscribe", { email, interests });
      const opLabel = operator === "any" ? "any UK operator" : operator;
      toast.success(
        r.data.status === "already_subscribed"
          ? `Already on the list — we'll flag ${opLabel} trainee driver roles too.`
          : `Subscribed. We'll email you when ${opLabel} posts a trainee driver role.`
      );
      setEmail("");
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="operator-subscribe-widget"
      className="bg-navy text-bone rounded-lg p-6 md:p-8 border border-amber/30 relative overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-amber/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative grid md:grid-cols-[1fr_1.2fr] gap-6 md:gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-amber font-bold">
            <Bell className="w-3.5 h-3.5" /> Free job alert
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-2 leading-[1.15]">
            {heading}
          </h3>
          <p className="text-bone/70 text-sm leading-relaxed mt-3">{subheading}</p>
          <p className="text-bone/40 text-[11px] mt-3">No spam. Unsubscribe in one click.</p>
        </div>

        {done ? (
          <div className="bg-bone/5 border border-amber/40 rounded-lg p-6 text-center" data-testid="op-subscribe-success">
            <div className="font-display font-bold text-lg">You're on the list ✅</div>
            <p className="text-sm text-bone/70 mt-2">
              We'll email you the moment a matching trainee train driver vacancy is posted.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="op-subscribe-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Operator</label>
              <select
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 text-sm rounded-md bg-bone/5 border border-amber/20 text-bone outline-none focus:border-amber transition-colors appearance-none cursor-pointer"
                data-testid="op-subscribe-operator"
              >
                <option value="any" className="bg-navy text-bone">Any UK operator</option>
                {operators.map((op) => (
                  <option key={op} value={op} className="bg-navy text-bone">{op}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Email</label>
              <input
                type="email"
                required
                placeholder="you@example.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 text-sm rounded-md bg-bone/5 border border-amber/20 text-bone placeholder:text-bone/40 outline-none focus:border-amber transition-colors"
                data-testid="op-subscribe-email"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-5 py-3 rounded-md bg-amber text-ink font-bold text-sm hover:bg-lime-600 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="op-subscribe-submit"
            >
              {loading ? "Subscribing…" : <>Notify me <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
